/**
 * Thrown by `authenticatedFetch` when the backend returns HTTP 401.
 *
 * Callers may catch this specifically to pause their own work (e.g. autosave
 * loops that would otherwise hammer the API). The global "session expired"
 * banner is triggered by the interceptor itself, so most callers do not need
 * to do any UX work — they just need to stop.
 */
export class AuthExpiredError extends Error {
  constructor(message = 'Authentication expired') {
    super(message)
    this.name = 'AuthExpiredError'
  }
}

/**
 * `message` が **そのままユーザーに見せられる日本語** であることを表す基底クラス（#765）。
 *
 * 通常のエラーは `utils/apiErrors.ts` の `toUserMessage()` が `errorCode` や
 * HTTP ステータスから文言を引くが、クライアント側で検出した失敗
 * （実測サイズを含むメッセージなど）は**サーバに聞くまでもなく文面が確定している**。
 * その区別を型で表す。`toUserMessage()` はこれを見つけたら `message` を素通しする。
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserFacingError'
  }
}

/**
 * 表紙画像が上限を超えていて、そもそも送れない (#762)。
 *
 * 実測サイズと上限を文面に入れて、ユーザーに見せて止める。
 */
export class CoverTooLargeError extends UserFacingError {
  constructor(message: string) {
    super(message)
    this.name = 'CoverTooLargeError'
  }
}

/**
 * 表紙画像を S3 へ直接アップロードできなかった (#955)。
 *
 * 以前はここで body 経由の旧経路へ落としていたが、SFN 経路は body の画像を
 * 運べないので**表紙の無い作品が「成功」で公開されていた**。いまは止めて
 * やり直してもらう（MP はまだ引かれていない）。
 */
export class CoverUploadFailedError extends UserFacingError {
  constructor(message = '表紙の画像をアップロードできませんでした。通信の具合を確かめて、もう一度お試しください。') {
    super(message)
    this.name = 'CoverUploadFailedError'
  }
}
